import { useState, useEffect, useCallback } from 'react';
import { campaignApi } from '../api/campaigns.api';
import {
    Campaign,
    CampaignCreate,
    CampaignUpdate,
    CampaignStatus,
    PaginatedResponse,
    CampaignStats,
} from '../types/campaign.types';
import { handleApiError } from '../api/client';

interface UseCampaignsResult {
    campaigns: Campaign[];
    loading: boolean;
    error: string | null;
    pagination: {
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
        hasNext: boolean;
        hasPrev: boolean;
    };
    fetchCampaigns: (params?: {
        page?: number;
        pageSize?: number;
        status?: CampaignStatus;
        search?: string;
    }) => Promise<void>;
    createCampaign: (data: CampaignCreate) => Promise<Campaign | null>;
    updateCampaign: (id: number, data: CampaignUpdate) => Promise<Campaign | null>;
    deleteCampaign: (id: number) => Promise<boolean>;
    stopCampaign: (id: number) => Promise<boolean>;
    refreshCampaigns: () => Promise<void>;
}

export const useCampaigns = (): UseCampaignsResult => {
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        pageSize: 50,
        totalPages: 0,
        hasNext: false,
        hasPrev: false,
    });

    const fetchCampaigns = useCallback(async (params?: {
        page?: number;
        pageSize?: number;
        status?: CampaignStatus;
        search?: string;
    }) => {
        try {
            setLoading(true);
            setError(null);

            const response: PaginatedResponse<Campaign> = await campaignApi.list({
                page: params?.page || 1,
                page_size: params?.pageSize || 50,
                status_filter: params?.status,
                search: params?.search,
            });

            setCampaigns(response.items);
            setPagination({
                total: response.total,
                page: response.page,
                pageSize: response.page_size,
                totalPages: response.total_pages,
                hasNext: response.has_next,
                hasPrev: response.has_prev,
            });
        } catch (err) {
            const errorMessage = handleApiError(err);
            setError(errorMessage);
            console.error('Error fetching campaigns:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const createCampaign = async (data: CampaignCreate): Promise<Campaign | null> => {
        try {
            setLoading(true);
            setError(null);
            const newCampaign = await campaignApi.create(data);

            // Refresh the list
            await fetchCampaigns();

            return newCampaign;
        } catch (err) {
            const errorMessage = handleApiError(err);
            setError(errorMessage);
            console.error('Error creating campaign:', err);
            return null;
        } finally {
            setLoading(false);
        }
    };

    const updateCampaign = async (
        id: number,
        data: CampaignUpdate
    ): Promise<Campaign | null> => {
        try {
            setLoading(true);
            setError(null);
            const updatedCampaign = await campaignApi.update(id, data);

            // Update local state
            setCampaigns((prev) =>
                prev.map((c) => (c.id === id ? updatedCampaign : c))
            );

            return updatedCampaign;
        } catch (err) {
            const errorMessage = handleApiError(err);
            setError(errorMessage);
            console.error('Error updating campaign:', err);
            return null;
        } finally {
            setLoading(false);
        }
    };

    const deleteCampaign = async (id: number): Promise<boolean> => {
        try {
            setLoading(true);
            setError(null);
            await campaignApi.delete(id);

            // Remove from local state
            setCampaigns((prev) => prev.filter((c) => c.id !== id));

            return true;
        } catch (err) {
            const errorMessage = handleApiError(err);
            setError(errorMessage);
            console.error('Error deleting campaign:', err);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const stopCampaign = async (id: number): Promise<boolean> => {
        try {
            setLoading(true);
            setError(null);
            const updated = await campaignApi.stop(id);
            setCampaigns((prev) => prev.map((c) => (c.id === id ? updated : c)));
            return true;
        } catch (err) {
            const errorMessage = handleApiError(err);
            setError(errorMessage);
            console.error('Error stopping campaign:', err);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const refreshCampaigns = useCallback(async () => {
        await fetchCampaigns();
    }, [fetchCampaigns]);

    // Initial fetch
    useEffect(() => {
        fetchCampaigns();
    }, [fetchCampaigns]);

    return {
        campaigns,
        loading,
        error,
        pagination,
        fetchCampaigns,
        createCampaign,
        updateCampaign,
        deleteCampaign,
        stopCampaign,
        refreshCampaigns,
    };
};

// Hook for campaign statistics
export const useCampaignStats = () => {
    const [stats, setStats] = useState<CampaignStats | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchStats = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await campaignApi.getStats();
            setStats(data);
        } catch (err) {
            const errorMessage = handleApiError(err);
            setError(errorMessage);
            console.error('Error fetching campaign stats:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    return { stats, loading, error, refreshStats: fetchStats };
};